import { Download } from 'lucide-react'
import { useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { DEFAULT_FILE_ICON, FILE_ICONS, fileExtension } from '@/lib/fileIcon'
import { documentContentUrl, type PreviewDocument } from './previewDocument'
import { SheetTable } from './SheetTable'
import { useDocumentTable } from './useDocumentTable'

/** A spreadsheet the browser can't render, drawn as a grid with a tab per sheet. */
export function SpreadsheetPreview({ doc }: { doc: PreviewDocument }) {
  const { data, isLoading, isError } = useDocumentTable(doc)
  const [sheetIndex, setSheetIndex] = useState(0)
  const sheet = data?.sheets[sheetIndex]
  const name = doc.fileName || 'Spreadsheet'
  const Icon = FILE_ICONS[fileExtension(doc.fileName)] ?? DEFAULT_FILE_ICON

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2">
        <Icon className="text-muted-foreground size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
        <Button size="icon-sm" variant="ghost" asChild>
          <a href={documentContentUrl(doc)} download={name} aria-label={`Download ${name}`} title="Download">
            <Download className="size-4" />
          </a>
        </Button>
      </div>
      {data && data.sheets.length > 1 && (
        <ToggleTabs
          value={String(sheetIndex)}
          onValueChange={(value) => setSheetIndex(Number(value))}
          items={data.sheets.map((each, index) => ({
            value: String(index),
            label: each.name || `Sheet ${index + 1}`,
          }))}
        />
      )}
      {isError ? (
        <Banner variant="error" small>
          This spreadsheet couldn’t be loaded.
        </Banner>
      ) : isLoading ? (
        <SkeletonRows rows={6} rowClassName="h-8" />
      ) : !sheet || sheet.rows.length === 0 ? (
        <p className="text-ink-500 text-sm">This sheet is empty.</p>
      ) : (
        <>
          <SheetTable rows={sheet.rows} />
          {sheet.totalRows > sheet.rows.length && (
            <p className="text-ink-500 text-xs">
              Showing the first {sheet.rows.length} of {sheet.totalRows} rows. Download the file for the rest.
            </p>
          )}
        </>
      )}
    </div>
  )
}
